'use client'

import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { useIsMounted } from 'hooks/use-is-mounted'
import { Slot } from 'primitives/slot'
import { tw } from 'utils/classnames'
import { StudioLoadingSpinner } from '../studio-loading-spinner'

export type StudioButtonVariant =
  | 'fill'
  | 'violet-aaa'
  | 'dark'
  | 'gray'
  | 'outline'
  | 'invisible'
  | 'danger'
  | 'green'
  | 'inverted'
export type StudioButtonSize = 'small' | 'medium' | 'tiny'
export type Gestures = 'scale' | 'none'

interface StudioButtonRootProps {
  variant?: StudioButtonVariant
  size?: StudioButtonSize
  gesture?: Gestures
  asChild?: boolean
  loading?: boolean
}

export type StudioButtonProps = Omit<React.ComponentProps<'button'>, 'ref'> &
  StudioButtonRootProps & {
    ref?: React.Ref<HTMLButtonElement>
  }

function StudioButtonRoot({
  size = 'medium',
  gesture = 'none',
  variant = 'fill',
  asChild,
  loading,
  ref,
  ...props
}: StudioButtonProps) {
  const className = tw(
    'rounded-2 outline-none',
    'relative isolate',
    'flex items-center justify-center',
    props.disabled && 'cursor-not-allowed',
    studioButtonClasses.sizes[size],
    studioButtonClasses.variants[variant],
    '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:transition-transform [&_svg]:duration-300 [&_svg]:ease-in-out',
    props.className,
  )

  const gestures = getGestureProps(gesture)

  const Comp = asChild ? Slot : 'button'

  const innerRef = React.useRef<HTMLButtonElement>(null)
  React.useImperativeHandle(ref, () => innerRef.current!)

  const hasSetLoading = React.useRef(false)
  React.useMemo(() => {
    if (loading) {
      hasSetLoading.current = true
    }
  }, [loading])

  return (
    <>
      <Comp {...gestures} {...props} className={tw(className)} ref={innerRef} />
      {hasSetLoading.current ? (
        <StudioButtonLoader containerRef={innerRef} loading={loading} size={size} />
      ) : null}
    </>
  )
}

function StudioButtonLoader({
  containerRef,
  loading,
  size,
}: {
  containerRef?: React.RefObject<HTMLButtonElement | null>
  loading?: boolean
  size: StudioButtonSize
}) {
  const isMounted = useIsMounted()

  if (!isMounted || !containerRef?.current) {
    return null
  }

  return ReactDOM.createPortal(
    <StudioLoadingSpinner loading={loading} size={size} />,
    containerRef.current,
  )
}

export const StudioButton = StudioButtonRoot

const studioButtonClasses = {
  variants: {
    fill: [
      'text-service-white-white bg-violet-500',
      'hover:bg-surface-action-primary-hover',
      'active:bg-violet-700 active:text-service-white-white focus:bg-violet-500 focus:text-service-white-white',
      'disabled:bg-gray-700 disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:hover:bg-gray-700 disabled:hover:text-theme_semantic_surface_ui_foreground-color-disabled',
    ].join(' '),
    invisible: [
      'bg-transparent',
      'hover:text-violet-500',
      'active:text-violet-700 focus:text-violet-700',
      'disabled:bg-transparent disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:hover:bg-transparent disabled:hover:text-theme_semantic_surface_ui_foreground-color-disabled',
    ].join(' '),
    'violet-aaa': [
      'bg-violet-700 text-service-white-white',
      'hover:bg-violet-600 hover:text-service-white-white',
      'active:bg-violet-700 active:text-service-white-white focus:bg-violet-700 focus:text-service-white-white',
      'disabled:bg-gray-700 disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:hover:bg-gray-700 disabled:hover:text-theme_semantic_surface_ui_foreground-color-disabled',
    ].join(' '),
    // This one is Dark in Figma, but bg-gray-900 results in a near-white background in white mode - might be a design mistake
    dark: [
      'bg-gray-900 text-gray-50',
      'hover:bg-surface-action-primary-hover hover:text-service-white-white',
      'active:bg-violet-700 active:text-service-white-white focus:bg-violet-700 focus:text-service-white-white',
      'disabled:bg-gray-700 disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:hover:bg-gray-700 disabled:hover:text-theme_semantic_surface_ui_foreground-color-disabled',
    ].join(' '),
    gray: [
      'bg-gray-500/70 backdrop-blur-sm text-service-white-white',
      'hover:text-service-white-white hover:bg-surface-action-primary-hover',
      'active:text-service-white-white active:bg-surface-action-primary-pressed focus:text-service-white-white focus:bg-violet-600',
      'disabled:bg-gray-700 disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:hover:bg-gray-700 disabled:hover:text-theme_semantic_surface_ui_foreground-color-disabled',
    ].join(' '),
    outline: [
      'bg-transparent border border-gray-700 text-gray-50',
      '[@media(pointer:fine)]:hover:border-violet-500 [@media(pointer:fine)]:hover:text-violet-500 active:text-violet-700',
      'active:border-violet-700 [@media(pointer:fine)]:focus:border-violet-700',
      'disabled:bg-gray-700 disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:hover:bg-gray-700 disabled:hover:text-theme_semantic_surface_ui_foreground-color-disabled disabled:border-transparent',
    ].join(' '),
    danger: [
      'text-service-white-white bg-red-100',
      'hover:bg-[#AF1133]',
      'active:bg-[#990827] focus:bg-[#990827]',
      'disabled:bg-gray-700 disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:hover:bg-gray-700 disabled:hover:text-theme_semantic_surface_ui_foreground-color-disabled',
    ].join(' '),
    green: [
      'text-service-white-white bg-green-500',
      'hover:bg-green-400',
      'active:bg-green-600 active:text-service-white-white focus:bg-green-500 focus:text-service-white-white',
      'disabled:bg-gray-700 disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:hover:bg-gray-700 disabled:hover:text-theme_semantic_surface_ui_foreground-color-disabled',
    ].join(' '),
    inverted: [
      'bg-gray-white-1000 text-gray-1000-white',
      'hover:bg-gray-200-600',
      'active:bg-gray-400-600',
      'disabled:bg-gray-700 disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:hover:bg-gray-700 disabled:hover:text-theme_semantic_surface_ui_foreground-color-disabled',
    ].join(' '),
  },
  sizes: {
    medium: ['h-10 px-4 gap-3 [&_svg]:size-6 [&_svg]:-mx-1 typography-14'].join(' '),
    small: ['h-8 px-3 gap-2 [&_svg]:size-5 [&_svg]:-mx-1 typography-13'].join(' '),
    tiny: ['h-6 px-2 gap-2 [&_svg]:size-4 [&_svg]:-mx-1 typography-12'].join(' '),
  },
}

function getGestureProps(gesture?: Gestures) {
  if (gesture === 'scale') {
    return {
      whileHover: {
        scale: 1.1,
      },
      whileTap: {
        scale: 1.05,
      },
    }
  }

  return {}
}

export type StudioIconButtonProps = Omit<React.ComponentProps<'button'>, 'ref'> & {
  size?: StudioButtonSize
  gesture?: Gestures
  variant?: StudioButtonVariant
  asChild?: boolean
  loading?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

export const StudioIconButton = function ({
  ref,
  size = 'medium',
  gesture = 'none',
  variant = 'fill',
  asChild,
  loading,
  ...props
}: StudioIconButtonProps) {
  const className = tw(
    'relative isolate',
    'rounded-2 outline-none',
    props.disabled && 'cursor-not-allowed',
    studioIconButtonClasses.sizes[size],
    studioIconButtonClasses.variants[variant],
    '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:transition-transform [&_svg]:duration-300 [&_svg]:ease-in-out',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-500',
    props.className,
  )

  const gestures = getGestureProps(gesture)

  const Comp = asChild ? Slot : 'button'

  const innerRef = React.useRef<HTMLButtonElement>(null)
  React.useImperativeHandle(ref, () => innerRef.current!)

  const hasSetLoading = React.useRef(false)
  React.useMemo(() => {
    if (loading) {
      hasSetLoading.current = true
    }
  }, [loading])

  return (
    <>
      <Comp {...gestures} {...props} className={className} ref={innerRef} />
      {hasSetLoading.current ? (
        <StudioButtonLoader containerRef={innerRef} loading={loading} size={size} />
      ) : null}
    </>
  )
}
StudioIconButton.displayName = 'StudioIconButton'

const studioIconButtonClasses = {
  variants: {
    fill: [
      'text-service-white-white bg-violet-500',
      'hover:bg-surface-action-primary-hover',
      'active:bg-violet-700 active:text-service-white-white focus:bg-violet-500 focus:text-service-white-white',
      'disabled:bg-gray-700 disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:hover:bg-gray-700 disabled:hover:text-theme_semantic_surface_ui_foreground-color-disabled',
    ].join(' '),
    invisible: [
      'bg-transparent',
      'hover:text-violet-500',
      'active:text-violet-700 focus:text-violet-700',
      'disabled:bg-transparent disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:hover:bg-transparent disabled:hover:text-theme_semantic_surface_ui_foreground-color-disabled',
    ].join(' '),
    'violet-aaa': [
      'bg-violet-700 text-service-white-white',
      'hover:bg-surface-action-primary-hover hover:text-service-white-white',
      'active:bg-violet-700 active:text-service-white-white focus:bg-violet-700 focus:text-service-white-white',
      'disabled:bg-gray-700 disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:hover:bg-gray-700 disabled:hover:text-theme_semantic_surface_ui_foreground-color-disabled',
    ].join(' '),
    dark: [
      'bg-gray-900 text-gray-50',
      'hover:bg-surface-action-primary-hover hover:text-service-white-white',
      'active:bg-violet-700 active:text-service-white-white focus:bg-violet-700 focus:text-service-white-white',
      'disabled:bg-gray-700 disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:hover:bg-gray-700 disabled:hover:text-theme_semantic_surface_ui_foreground-color-disabled',
    ].join(' '),
    gray: [
      'bg-gray-500/70 backdrop-blur-sm text-service-white-white',
      'hover:text-service-white-white hover:bg-surface-action-primary-hover',
      'active:text-service-white-white active:bg-violet-700 focus:text-service-white-white focus:bg-violet-700',
      'disabled:bg-gray-700 disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:hover:bg-gray-700 disabled:hover:text-theme_semantic_surface_ui_foreground-color-disabled',
    ].join(' '),
    outline: [
      'bg-transparent text-gray-50 border border-gray-700 text-gray-50',
      'hover:border-violet-500 hover:text-violet-500 active:text-violet-700',
      'active:border-violet-700 focus:border-violet-700',
      'disabled:bg-transparent disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:border-gray-400-400',
    ].join(' '),
    danger: [
      'text-service-white-white bg-red-100',
      'hover:bg-[#AF1133]',
      'active:bg-[#990827] focus:bg-[#990827]',
      'disabled:bg-gray-700 disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:hover:bg-gray-700 disabled:hover:text-theme_semantic_surface_ui_foreground-color-disabled',
    ].join(' '),
    green: [
      'text-service-white-white bg-green-500',
      'hover:bg-green-400',
      'active:bg-green-600 active:text-service-white-white focus:bg-green-500 focus:text-service-white-white',
      'disabled:bg-gray-700 disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:hover:bg-gray-700 disabled:hover:text-theme_semantic_surface_ui_foreground-color-disabled',
    ].join(' '),
    inverted: [
      'bg-gray-white-1000 text-gray-1000-white',
      'hover:bg-gray-200-600',
      'active:bg-gray-400-600',
      'disabled:bg-gray-700 disabled:text-theme_semantic_surface_ui_foreground-color-disabled disabled:hover:bg-gray-700 disabled:hover:text-theme_semantic_surface_ui_foreground-color-disabled',
    ].join(' '),
  },
  sizes: {
    medium: ['flex h-10 w-10 items-center justify-center px-2 [&>svg]:size-6'].join(' '),
    small: ['flex h-8 w-8 items-center justify-center px-1 [&>svg]:size-5'].join(' '),
    tiny: ['flex h-6 w-6 items-center justify-center px-1 [&>svg]:size-4'].join(' '),
  },
}
